import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import '../../style/scss/account.scss';

interface TwoFactorEnrollmentProps {
    onSuccess: (redirectTo: string | null) => void;
    requirePassword?: boolean;
}

// Shared enrollment component that shows the qr code and confirms the first code
const TwoFactorEnrollment: React.FC<TwoFactorEnrollmentProps> = ({ onSuccess, requirePassword = false }) => {
    const { t } = useTranslation();
    const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
    const [secret, setSecret] = useState<string | null>(null);
    const [code, setCode] = useState<string>('');
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [passwordConfirmed, setPasswordConfirmed] = useState<boolean>(!requirePassword);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    // Generate a pending secret and provisioning uri, optionally with the current password
    const startEnrollment = async (password?: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await saveData({
                apiUrl: 'post_two_factor_setup',
                method: 'post',
                data: password ? { current_password: password } : {}
            });
            if (response) {
                setOtpauthUri(response.data.otpauth_uri);
                setSecret(response.data.secret);
                setPasswordConfirmed(true);
            }
        } catch (error) {
            // Show the invalid password error when re linking requires the password
            setError(requirePassword ? 'account.two_factor_invalid_password' : 'account.two_factor_invalid_code');
        } finally {
            setLoading(false);
        }
    };

    // Start the enrollment directly when no password confirmation is required
    useEffect(() => {
        if (!requirePassword) {
            startEnrollment();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Confirm the first code to activate two factor authentication
    const confirmEnrollment = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await saveData({
                apiUrl: 'post_two_factor_confirm',
                method: 'post',
                data: { 'twofactor-code': code }
            });
            if (response) {
                onSuccess(response.data.redirect_to || null);
            }
        } catch (error) {
            setError('account.two_factor_invalid_code');
        } finally {
            setLoading(false);
        }
    };

    // Ask for the current password first when re linking an existing setup
    if (!passwordConfirmed) {
        return (
            <div className='two-factor-enrollment'>
                <p>{t('account.two_factor_password_description')}</p>

                {/* Input to confirm the current password */}
                <input
                    type='password'
                    autoComplete='current-password'
                    placeholder={t('account.two_factor_password_placeholder')}
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                />

                {/* Show the error when the password is invalid */}
                {error && (
                    <p className='two-factor-error'>{t(error)}</p>
                )}

                <PrimaryButton
                    label='account.two_factor_password_button_label'
                    size='large'
                    loading={loading}
                    onClick={() => startEnrollment(currentPassword)}
                />
            </div>
        );
    }

    return (
        <div className='two-factor-enrollment'>
            <p>{t('account.two_factor_setup_description')}</p>

            {/* Show the qr code to scan with an authenticator app */}
            {otpauthUri && (
                <div className='two-factor-qr'>
                    <QRCodeSVG value={otpauthUri} size={180} />
                </div>
            )}

            {/* Show the secret to enter manually if scanning is not possible */}
            {secret && (
                <p className='two-factor-secret'>
                    {t('account.two_factor_setup_manual_secret')}<br />
                    <span className='two-factor-secret-value'>{secret}</span>
                </p>
            )}

            {/* Input to confirm the first generated code */}
            <input
                type='text'
                inputMode='numeric'
                autoComplete='one-time-code'
                placeholder={t('account.two_factor_placeholder')}
                value={code}
                onChange={(event) => setCode(event.target.value)}
            />

            {/* Show the error when the code is invalid */}
            {error && (
                <p className='two-factor-error'>{t(error)}</p>
            )}

            <PrimaryButton
                label='account.two_factor_confirm_button_label'
                size='large'
                loading={loading}
                onClick={confirmEnrollment}
            />
        </div>
    );
};

export default TwoFactorEnrollment;
